import React, { useMemo, useCallback } from 'react'
import { Box, CircularProgress, TextField } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import omit from 'lodash/omit'
import { useTranslation } from 'gatsby-plugin-react-i18next'
import {
  Modal as ContentfulModalType,
  ModalStep,
  ModalVariants,
} from '@talentinc/gatsby-theme-ecom/types/modal'
import {
  CustomElementMap,
  ContentfulType,
} from '@talentinc/gatsby-theme-ecom/types/references'
import { ButtonRole } from '@talentinc/gatsby-theme-ecom/types/page'
import RichText from '@talentinc/gatsby-theme-ecom/components/RichText'
import MultiStepModal from './MultiStepModal'
import useMultiStepModal from './useMultiStepModal'
import ResumeUploadButton, {
  ResumeUploadButtonCallback,
  ResumeUploadButtonEventType,
} from '../Buttons/ResumeUpload'
import ContentfulButton from '../Buttons/ContentfulButton'
import CustomComponent from '../CustomComponent'
import useLeadActions from '@talentinc/gatsby-theme-ecom/hooks/useLeadActions'
import LanguageButton from '../LanguageSwitcher/LanguageButton'

const ContentfulModal: React.FC<ContentfulModalType> = ({
  steps,
  variant,
  color,
  showCloseButton = false,
}) => {
  return (
    <MultiStepModal
      steps={steps.map((step) => (
        <>
          {step.__typename === 'ContentfulModalStep' && (
            <ContentfulModalStep
              key={step.contentful_id}
              variant={variant}
              {...step}
            />
          )}
          {step.__typename === 'ContentfulCustomComponent' && (
            <Box>
              <CustomComponent item={step} />
            </Box>
          )}
        </>
      ))}
      color={color}
      showCloseButton={showCloseButton}
      variant={variant}
    />
  )
}

export const ContentfulModalStep: React.FC<
  ModalStep & {
    variant: ModalVariants | null
  }
> = ({ content, variant }) => {
  const { advanceStep, handleClose } = useMultiStepModal()
  const {
    setResumeFile,
    setEmail,
    resumeSubmission,
    newsletterSubmission,
    state,
    errors,
  } = useLeadActions()
  const { classes } = useStyles()
  const { t } = useTranslation()

  const isSubmitting =
    variant === ModalVariants.ResumeUpload
      ? resumeSubmission.state.loading
      : newsletterSubmission.state.loading

  const handleFileSelect = useCallback<ResumeUploadButtonCallback>(
    (event: ResumeUploadButtonEventType) => {
      let file: File | null = null

      if ('files' in event.target && event.target.files?.[0]) {
        file = event.target.files[0]
      } else if ('dataTransfer' in event && event.dataTransfer.files?.[0]) {
        file = event.dataTransfer.files[0]
        // On MacOS (at least) dropping a file like this without preventDefault
        // will cause the document to open in a new tab.
        event.preventDefault()
      }

      if (!file) {
        return
      }

      if (setResumeFile(file)) {
        advanceStep()
      }
    },
    [setResumeFile, advanceStep]
  )

  const handleEmailChange = useCallback<
    React.ChangeEventHandler<HTMLInputElement>
  >(
    (event) => {
      setEmail(event.target.value)
    },
    [setEmail]
  )

  const handleSubmit = useCallback<React.MouseEventHandler<HTMLButtonElement>>(
    async (event) => {
      switch (variant) {
        case ModalVariants.ResumeUpload:
          // This could be triggered by a form submission and that will cause
          // the page to refresh unless blocked
          event.preventDefault()

          if (!resumeSubmission.valid) {
            return false
          }

          const res = await resumeSubmission.submit()

          if (!res) {
            return false
          }

          if (res.status === 200) {
            advanceStep()
          }
          break
      }
    },
    [variant, resumeSubmission, advanceStep]
  )

  const customElements = useMemo<Partial<CustomElementMap>>(
    () => ({
      [ContentfulType.Button]: ({ reference: button }) => {
        switch (button.role) {
          case ButtonRole.ResumeUpload:
            return (
              <ResumeUploadButton
                onFileSelect={handleFileSelect}
                uploadClass={classes.upload}
                error={errors.resumeFile}
                ButtonProps={{
                  fullWidth: true,
                }}
                mixpanelTracking={button.mixpanelTracking}
              >
                {button.buttonText}
              </ResumeUploadButton>
            )
          case ButtonRole.Dismiss:
            return (
              <ContentfulButton
                className={classes.buttonDismiss}
                onClick={handleClose}
                fullWidth
                data-qa-modal-dismiss-button
                {...omit(button, ['role', 'action'])}
              />
            )
          case ButtonRole.Submit:
            return (
              <ContentfulButton
                className={classes.button}
                onClick={handleSubmit}
                disabled={!resumeSubmission.valid}
                type="submit"
                data-qa-modal-submission-button
                endIcon={
                  isSubmitting && (
                    <CircularProgress size="1em" color="inherit" />
                  )
                }
                buttonText={
                  isSubmitting
                    ? t('contentfulModal.submitting')
                    : button.buttonText
                }
                {...omit(button, ['role', 'action', 'buttonText'])}
              />
            )
          case ButtonRole.EmailInput:
            return (
              <TextField
                variant="standard"
                className={classes.textField}
                InputProps={{
                  className: classes.input,
                  classes: {
                    error: classes.emailInputError,
                  },
                  placeholder: button.buttonText,
                }}
                FormHelperTextProps={{
                  classes: {
                    root: classes.helperText,
                    error: classes.helperTextError,
                  },
                }}
                inputProps={{ className: classes.inputElement }}
                data-qa-modal-email-input
                type="email"
                required
                onChange={handleEmailChange}
                value={state.email}
                {...(variant === ModalVariants.ResumeUpload &&
                !!resumeSubmission.state.error
                  ? {
                      error: true,
                      helperText: t(
                        'contentfulModal.resumeUploadSubmissionFailure'
                      ),
                    }
                  : {})}
              />
            )
          case ButtonRole.LanguageSwitcher:
            return <LanguageButton button={button} />
          default:
            return null
        }
      },
    }),
    [
      handleClose,
      handleEmailChange,
      handleFileSelect,
      handleSubmit,
      classes,
      state.email,
      isSubmitting,
      resumeSubmission.valid,
      t,
      errors.resumeFile,
      resumeSubmission.state.error,
      variant,
    ]
  )

  // Wrapped in a form so that pressing enter can submit easily
  return (
    <form>
      <RichText
        rawBody={content.raw}
        references={content.references}
        options={{
          customElements,
        }}
      />
    </form>
  )
}

const useStyles = makeStyles()((theme) => ({
  button: {
    width: '100%',

    'br + &': {
      marginTop: '1em',
    },
  },
  buttonDismiss: {
    marginTop: '.75em',
  },
  input: {
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
  },
  emailInputError: {
    '& input': {
      borderColor: theme.palette.secondary.light,
    },
  },
  inputElement: {
    padding: '.88em 1em',
    textAlign: 'center',
  },
  textField: {
    width: '100%',

    '&:not(first-child)': {
      marginTop: '2em',
    },
  },
  helperText: {
    textAlign: 'center',
  },
  helperTextError: {
    '&&': {
      color: theme.palette.secondary.light,
    },
  },
  upload: {
    margin: '2.59em -3.41em -2.99em',
    padding: '2.53em 3.41em 2.99em',
    minHeight: '292px',
    height: '100%',

    [theme.breakpoints.down('md')]: {
      padding: '2.53em 1.5em 2.34em',
      margin: '2.59em -1.5em -3.74em',
    },
  },
}))

export default ContentfulModal
